import React from 'react';
import styled from 'styled-components';
import { colors } from '../../Shared/colors';
import { useGetMeetingTasksQuery } from '../../features/employeeSlice';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions,
  Chip,
  IconButton,
  Typography,
  Divider,
  Skeleton,
  Tooltip
} from '@mui/material';
import {
  Close as CloseIcon,
  CalendarToday as CalendarIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Assignment as AssignmentIcon,
  CheckCircle as CheckCircleIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import moment from 'moment';

export const MeetingTasksModal = ({ open, onClose, staff }) => {
  const { data: meetingData, isFetching } = useGetMeetingTasksQuery({
    staffId: staff?.id
  }, {
    skip: !open
  });

  const handleClose = () => {
    onClose();
  };

  const exportToCSV = () => {
    if (!meetingData?.tasks) return;
    
    const csvContent = [
      ['Lead ID', 'Client Name', 'Phone', 'Type', 'Sub Type', 'Employee', 'Created Date', 'Completed Date', 'Comments'],
      ...meetingData.tasks.map(task => [
        task.leadID || 'N/A',
        task.clientName || 'Unknown',
        task.clientPhone || 'N/A',
        task.currentTask.currentTaskType || 'N/A',
        task.currentTask.currentSubTypeTask || 'N/A',
        task.currentTask.empName || 'N/A',
        moment(task.createdAt).format('MMM DD, YYYY'),
        task.currentTask.completedDate ? moment(task.currentTask.completedDate).format('MMM DD, YYYY') : 'Not completed',
        task.currentTask.comment || 'No comments'
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `meeting-tasks-${moment().format('YYYY-MM-DD')}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <StyledDialog 
      open={open} 
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '20px',
          maxHeight: '90vh',
          margin: '20px'
        }
      }}
    >
      <DialogHeader>
        <DialogTitle>
          <TitleContainer>
            <TitleIcon>
              <CalendarIcon />
            </TitleIcon>
            <TitleContent>
              <TitleText>Meeting Tasks</TitleText>
              <SubtitleText>
                {staff ? `${staff.label}'s meetings` : 'Your meetings'}
              </SubtitleText>
            </TitleContent>
          </TitleContainer>
        </DialogTitle>
        <DialogActions>
          <Tooltip title="Export to CSV">
            <IconButton onClick={exportToCSV} size="small">
              <DownloadIcon />
            </IconButton>
          </Tooltip>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogActions>
      </DialogHeader>

      <DialogContent>
        <HeaderNote>
          Showing completed meeting tasks only
        </HeaderNote>

        <Divider sx={{ margin: '12px 0' }} />

        {isFetching ? (
          <LoadingContainer>
            {[...Array(3)].map((_, index) => (
              <Skeleton key={index} variant="rectangular" height={120} sx={{ borderRadius: '12px', marginBottom: '16px' }} />
            ))}
          </LoadingContainer>
        ) : !meetingData?.tasks ? (
          <ErrorContainer>
            <Typography color="error">Failed to load meeting tasks</Typography>
          </ErrorContainer>
        ) : meetingData?.tasks?.length === 0 ? (
          <EmptyContainer>
            <CalendarIcon sx={{ fontSize: 64, color: colors.textMed, marginBottom: '16px' }} />
            <Typography variant="h6" color="textSecondary">
              No completed meetings found
            </Typography>
            <Typography variant="body2" color="textSecondary">Try a different staff member or timeframe.</Typography>
          </EmptyContainer>
        ) : (
          <TasksContainer>
            {meetingData?.tasks?.map((task, index) => (
              <TaskCard key={task._id || index}>
                <TaskHeader>
                  <TaskInfo>
                    <TaskTitle>
                      <PersonIcon sx={{ fontSize: 20, marginRight: '8px', color: colors.brown }} />
                      {task.clientName || 'Unknown Client'}
                    </TaskTitle>
                    <TaskSubtitle>
                      Lead ID: {task.leadID || 'N/A'}
                    </TaskSubtitle>
                  </TaskInfo>
                  <StatusChip 
                    icon={<CheckCircleIcon />} 
                    label="Completed" 
                    color="success" 
                    variant="filled"
                  />
                </TaskHeader>

                <TaskDetails>
                  <DetailGrid>
                    <DetailItem>
                      <DetailLabel>Type</DetailLabel>
                      <DetailValue>{task.currentTask.currentTaskType || 'N/A'}</DetailValue>
                    </DetailItem>
                    <DetailItem>
                      <DetailLabel>Sub Type</DetailLabel>
                      <DetailValue>{task.currentTask.currentSubTypeTask || 'N/A'}</DetailValue>
                    </DetailItem>
                    <DetailItem>
                      <DetailLabel>Employee</DetailLabel>
                      <DetailValue>{task.currentTask.empName || 'N/A'}</DetailValue>
                    </DetailItem>
                    <DetailItem>
                      <DetailLabel>Completed</DetailLabel>
                      <DetailValue>{task.currentTask.completedDate ? moment(task.currentTask.completedDate).format('MMM DD, YYYY [at] h:mm A') : 'N/A'}</DetailValue>
                    </DetailItem>
                  </DetailGrid>

                  <DetailRow>
                    <DetailIcon>
                      <PhoneIcon />
                    </DetailIcon>
                    <DetailText>{task.clientPhone || 'N/A'}</DetailText>
                  </DetailRow>
                  
                  <DetailRow>
                    <DetailIcon>
                      <CalendarIcon />
                    </DetailIcon>
                    <DetailText>
                      Created: {moment(task.createdAt).format('MMM DD, YYYY')}
                    </DetailText>
                  </DetailRow>

                  {task.currentTask.completedDate && (
                    <DetailRow>
                      <DetailIcon>
                        <CheckCircleIcon />
                      </DetailIcon>
                      <DetailText>
                        Completed: {moment(task.currentTask.completedDate).format('MMM DD, YYYY [at] h:mm A')}
                      </DetailText>
                    </DetailRow>
                  )}

                  {task.currentTask.comment && (
                    <DetailRow>
                      <DetailIcon>
                        <AssignmentIcon />
                      </DetailIcon>
                      <DetailText>
                        <CommentText>{task.currentTask.comment}</CommentText>
                      </DetailText>
                    </DetailRow>
                  )}

                  {task.currentTask.attachment && (
                    <DetailRow>
                      <DetailIcon>
                        <AssignmentIcon />
                      </DetailIcon>
                      <DetailText>
                        <AttachmentLink href={task.currentTask.attachment} target="_blank" rel="noopener noreferrer">
                          View Attachment
                        </AttachmentLink>
                      </DetailText>
                    </DetailRow>
                  )}
                </TaskDetails>
              </TaskCard>
            ))}
          </TasksContainer>
        )}
      </DialogContent>
    </StyledDialog>
  );
};

// Styled Components
const StyledDialog = styled(Dialog)`
  .MuiDialog-paper {
    background: ${colors.white};
    box-shadow: 
      0 25px 50px -12px rgba(0, 0, 0, 0.25),
      0 0 0 1px rgba(0, 0, 0, 0.05);
  }
`;

const DialogHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 24px 0 24px;
  border-bottom: 1px solid ${colors.inputBorderWhite};
  background: linear-gradient(135deg, ${colors.lightWhite} 0%, ${colors.white} 100%);
  border-radius: 20px 20px 0 0;
`;

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const TitleIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: linear-gradient(135deg, ${colors.brown} 0%, #1a6b6b 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${colors.white};
  box-shadow: 0 4px 12px rgba(36, 147, 147, 0.3);
`;

const TitleContent = styled.div`
  display: flex;
  flex-direction: column;
`;

const TitleText = styled.h2`
  margin: 0;
  font-size: 1.5rem;
  font-weight: 700;
  color: ${colors.text};
  line-height: 1.2;
`;

const SubtitleText = styled.p`
  margin: 4px 0 0 0;
  font-size: 0.875rem;
  color: ${colors.textMed};
  font-weight: 400;
`;

const HeaderNote = styled.div`
  font-size: 0.85rem;
  color: ${colors.textMed};
`;

const LoadingContainer = styled.div`
  padding: 20px 0;
`;

const ErrorContainer = styled.div`
  padding: 40px 20px;
  text-align: center;
`;

const EmptyContainer = styled.div`
  padding: 60px 20px;
  text-align: center;
  background: ${colors.lightWhite};
  border-radius: 16px;
  border: 1px solid ${colors.inputBorderWhite};
`;

const TasksContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-height: 400px;
  overflow-y: auto;
  padding-right: 8px;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: ${colors.lightWhite};
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${colors.brown};
    border-radius: 3px;
  }
`;

const TaskCard = styled.div`
  background: ${colors.white};
  border-radius: 16px;
  padding: 20px;
  border: 1px solid ${colors.inputBorderWhite};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
    border-color: ${colors.brown};
  }
`;

const TaskHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
`;

const TaskInfo = styled.div`
  flex: 1;
`;

const TaskTitle = styled.div`
  display: flex;
  align-items: center;
  font-size: 1.1rem;
  font-weight: 600;
  color: ${colors.text};
  margin-bottom: 4px;
`;

const TaskSubtitle = styled.div`
  font-size: 0.875rem;
  color: ${colors.textMed};
  font-weight: 500;
`;

const StatusChip = styled(Chip)`
  &.MuiChip-filled {
    font-weight: 600;
    font-size: 0.8rem;
  }
`;

const TaskDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 12px;
  padding: 8px 0;
`;

const DetailItem = styled.div`
  background: ${colors.lightWhite};
  border: 1px solid ${colors.inputBorderWhite};
  border-radius: 8px;
  padding: 10px 12px;
`;

const DetailLabel = styled.div`
  font-size: 0.75rem;
  color: ${colors.textMed};
  text-transform: uppercase;
  letter-spacing: 0.4px;
`;

const DetailValue = styled.div`
  font-size: 0.9rem;
  color: ${colors.text};
  font-weight: 600;
`;

const DetailRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
`;

const DetailIcon = styled.div`
  width: 20px;
  height: 20px;
  color: ${colors.brown};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 2px;
`;

const DetailText = styled.div`
  flex: 1;
  font-size: 0.875rem;
  color: ${colors.text};
  line-height: 1.4;
`;

const CommentText = styled.div`
  font-style: italic;
  color: ${colors.textMed};
  background: ${colors.lightWhite};
  padding: 8px 12px;
  border-radius: 8px;
  border-left: 3px solid ${colors.brown};
`;

const AttachmentLink = styled.a`
  color: ${colors.brown};
  font-weight: 600;
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;